import React, { useState } from 'react';
import { Modal, Input, Button, Typography, Alert } from 'antd';
import { supabase, testSupabaseConnection } from '../lib/supabase';

const { Text, Title } = Typography;

interface AccessCodeModalProps {
  open: boolean;
  onSuccess: () => void;
  onCancel: () => void;
  userId: string;
}

export const AccessCodeModal: React.FC<AccessCodeModalProps> = ({
  open,
  onSuccess,
  onCancel,
  userId,
}) => {
  const [accessCode, setAccessCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleActivate = async () => {
    if (!accessCode.trim()) {
      setError('Please enter an access code');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log('Attempting to activate with code:', accessCode.trim());
      
      // Check if Supabase is configured
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
      console.log('Supabase URL configured:', !!supabaseUrl);
      console.log('Supabase Key configured:', !!supabaseKey);
      
      if (!supabaseUrl || !supabaseKey) {
        setError('Supabase is not configured. Please check your environment variables.');
        setLoading(false);
        return;
      }
      
      console.log('Supabase client:', supabase);
      
      // Test Supabase client connectivity first (skips getSession which hangs)
      console.log('=== Testing Supabase client connectivity ===');
      let connectionTest;
      try {
        const testPromise = testSupabaseConnection();
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Connection test timed out')), 10000)
        );
        connectionTest = await Promise.race([testPromise, timeoutPromise]) as any;
        console.log('Connection test result:', connectionTest);
      } catch (testErr: any) {
        console.error('Connection test failed or timed out:', testErr);
        connectionTest = { connected: false, error: testErr };
      }
      console.log('=== End connection test ===');
      
      if (!connectionTest?.connected) {
        console.error('Supabase client test failed:', connectionTest?.error);
        const errorMsg = connectionTest?.error?.message || connectionTest?.error || 'Connection test failed';
        setError(`Supabase connection failed: ${errorMsg}. Please check your network and Supabase configuration.`);
        setLoading(false);
        return;
      }
      
      console.log('Supabase connection test passed! RPC mechanism is working.');
      
      // Note: We skip session check because:
      // 1. The RPC function validates auth.uid() internally
      // 2. getSession() hangs consistently
      // 3. The RPC will return a proper error if user is not authenticated
      console.log('Skipping session check - RPC will handle authentication');

      // Call RPC function to validate and activate
      console.log('Calling RPC function with code:', accessCode.trim());
      console.log('RPC function name: activate_account_with_code');
      console.log('RPC parameters:', { code_input: accessCode.trim() });
      
      // Log before making the call to verify we reach this point
      const beforeCallTime = Date.now();
      console.log('About to call supabase.rpc at:', beforeCallTime);
      
      let data, error;
      try {
        // Make the RPC call
        const rpcResult = await supabase.rpc('activate_account_with_code', {
          code_input: accessCode.trim()
        });
        
        const afterCallTime = Date.now();
        console.log('RPC call returned at:', afterCallTime);
        console.log('Time taken:', afterCallTime - beforeCallTime, 'ms');
        
        data = rpcResult.data;
        error = rpcResult.error;
        console.log('RPC call completed - result object:', rpcResult);
      } catch (rpcErr) {
        console.error('RPC call threw exception:', rpcErr);
        console.error('Exception details:', JSON.stringify(rpcErr, null, 2));
        setError('Failed to connect to server. Please check your connection.');
        setLoading(false);
        return;
      }

      console.log('RPC response - data:', data, 'error:', error);

      if (error) {
        console.error('RPC error details:', error);
        setError(error.message || 'Failed to activate account. Please try again.');
        setLoading(false);
        return;
      }

      // Check the result from RPC
      if (!data || !data.success) {
        const errorMsg = data?.error || 'Invalid or already used access code';
        console.error('Activation failed:', errorMsg);
        setError(errorMsg);
        setLoading(false);
        return;
      }

      // Success
      console.log('Activation successful!');
      setLoading(false);
      setAccessCode('');
      onSuccess();
    } catch (err) {
      console.error('Unexpected error during activation:', err);
      setError('An error occurred. Please try again.');
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <Title level={4} style={{ margin: 0, color: '#032539' }}>
          Activate Your Account
        </Title>
      }
      open={open}
      onCancel={onCancel}
      footer={null}
      width={500}
    >
      <div style={{ padding: '8px 0' }}>
        <Text style={{ color: '#032539', marginBottom: 16, display: 'block' }}>
          Enter your access code to activate your account and start chatting with experts.
        </Text>
        
        {error && (
          <Alert
            message={error}
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            closable
            onClose={() => setError(null)}
          />
        )}

        <Input
          placeholder="Enter access code"
          value={accessCode}
          onChange={(e) => {
            setAccessCode(e.target.value.toUpperCase());
            setError(null);
          }}
          onPressEnter={handleActivate}
          style={{
            marginBottom: 16,
            fontSize: '16px',
            textTransform: 'uppercase',
          }}
          maxLength={20}
        />

        <Button
          type="primary"
          onClick={handleActivate}
          loading={loading}
          block
          style={{
            backgroundColor: '#032539',
            borderColor: '#032539',
            color: '#FbF3F2',
            height: '40px',
          }}
        >
          Activate Account
        </Button>
      </div>
    </Modal>
  );
};

